import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from './api';
import type { DevUser } from './api';
import { useAuthStore } from './useAuthStore';

/**
 * DevUsersPanel — Panel de acceso rápido para entorno de desarrollo.
 *
 * @remarks
 * Solo se monta si `import.meta.env.DEV` es true (Vite dev server).
 * Consume GET /auth/dev/users-with-projects para obtener usuarios reales
 * con proyectos asignados desde la DB. No hay hardcoding de credenciales.
 *
 * El quick-login usa POST /auth/dev/quick-login (sin password).
 * Ambos endpoints retornan 403 si el backend no corre en NODE_ENV=development.
 */

/**
 * Etiqueta de rol con color semántico
 */
function RoleLabel({
  roleName,
  isSuperAdmin,
}: {
  roleName: string;
  isSuperAdmin: boolean;
}): JSX.Element {
  const cssClass = isSuperAdmin
    ? 'superadmin'
    : roleName === 'admin'
      ? 'admin'
      : roleName === 'editor'
        ? 'user-high'
        : 'user-low';

  const label = isSuperAdmin
    ? 'Superadmin'
    : roleName === 'admin'
      ? 'Admin'
      : roleName === 'editor'
        ? 'Editor'
        : 'Viewer';

  return <div className={`test-user-role ${cssClass}`}>{label}</div>;
}

/**
 * Resumen de proyectos para una tarjeta de usuario
 */
function ProjectsSummary({ projects }: { projects: DevUser['projects'] }): JSX.Element {
  if (projects.length === 0) {
    return <div className="dev-user-projects dev-user-projects--none">Sin proyecto asignado</div>;
  }

  if (projects.length === 1) {
    return (
      <div className="dev-user-projects" title={projects[0].name}>
        {projects[0].name}
      </div>
    );
  }

  return (
    <div className="dev-user-projects" title={projects.map(p => p.name).join(', ')}>
      {projects[0].name} <span className="dev-user-projects-more">+{projects.length - 1}</span>
    </div>
  );
}

/**
 * Tarjeta de usuario individual
 */
function DevUserCard({
  devUser,
  onLogin,
  isLoading,
}: {
  devUser: DevUser;
  onLogin: (userId: string) => void;
  isLoading: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={() => onLogin(devUser.userId)}
      className="test-user-btn"
      disabled={isLoading}
      title={`Login como ${devUser.email}`}
    >
      <RoleLabel roleName={devUser.roleName} isSuperAdmin={devUser.isSuperAdmin} />
      <div className="test-user-email">{devUser.displayName || devUser.email}</div>
      <div className="test-user-email" style={{ opacity: 0.65, fontSize: '10px' }}>
        {devUser.email}
      </div>
      <ProjectsSummary projects={devUser.projects} />
    </button>
  );
}

function getErrorMessage(err: unknown): string {
  const resp = (err as { response?: { data?: { message?: string }; status?: number } })?.response;
  return resp?.data?.message || 'Error al hacer login';
}

function persistDevToken(accessToken: string): void {
  localStorage.setItem(
    'auth-storage',
    JSON.stringify({
      state: { token: accessToken },
      version: 0,
    })
  );
}

/**
 * Componente principal del panel DEV.
 * Se auto-oculta si el entorno no es dev o si el endpoint retorna 403.
 */
export function DevUsersPanel(): JSX.Element | null {
  if (!import.meta.env.DEV) return null;
  return <DevUsersPanelInner />;
}

function DevUsersPanelInner(): JSX.Element | null {
  const navigate = useNavigate();
  const { isLoading: authLoading } = useAuthStore();
  const [devUsers, setDevUsers] = useState<DevUser[]>([]);
  const [fetchState, setFetchState] = useState<'idle' | 'loading' | 'ok' | 'error'>('loading');
  const [loginLoadingId, setLoginLoadingId] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    authApi
      .getDevUsers()
      .then(users => {
        if (!cancelled) {
          setDevUsers(users);
          setFetchState('ok');
        }
      })
      .catch(() => {
        if (!cancelled) setFetchState('error');
      });
    return (): void => {
      cancelled = true;
    };
  }, []);

  const handleQuickLogin = async (userId: string): Promise<void> => {
    setLoginLoadingId(userId);
    setLoginError(null);
    try {
      const response = await authApi.devQuickLogin(userId);
      persistDevToken(response.accessToken);
      useAuthStore.setState({
        user: response.user,
        token: response.accessToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      navigate('/dashboard');
    } catch (err) {
      setLoginError(getErrorMessage(err));
    } finally {
      setLoginLoadingId(null);
    }
  };

  if (fetchState === 'error') return null;
  if (fetchState === 'ok' && devUsers.length === 0) return null;

  const isAnyLoading = authLoading || loginLoadingId !== null;

  return (
    <div className="test-users-panel">
      <div className="test-users-header">
        <span className="test-badge">DEV</span>
        <span>Acceso Rapido</span>
      </div>
      {fetchState === 'loading' ? (
        <div className="test-users-footer">
          <small style={{ opacity: 0.6 }}>Cargando usuarios...</small>
        </div>
      ) : (
        <>
          <div className="test-users-grid">
            {devUsers.map(u => (
              <DevUserCard
                key={u.userId}
                devUser={u}
                onLogin={uid => void handleQuickLogin(uid)}
                isLoading={isAnyLoading}
              />
            ))}
          </div>
          <div className="test-users-footer">
            {loginError ? (
              <small style={{ color: '#ff6b6b' }}>{loginError}</small>
            ) : (
              <small>
                {devUsers.length} usuario{devUsers.length !== 1 ? 's' : ''} — clic para login
              </small>
            )}
          </div>
        </>
      )}
    </div>
  );
}
